/**
 * L3 — the RECORD side of the stage-colour spine.
 *
 * ===========================================================================
 * WHAT THE RAIL IS
 * ===========================================================================
 * A 3px vertical stripe in the inquiry's own stage colour, drawn at the left
 * edge of the peek panel and of the record page header. It is the thread that
 * ties an open record back to the board column it came from: the operator
 * clicked a card in an orange column, and the panel that opened has an orange
 * edge.
 *
 * It is an ACCENT, not the carrier of the fact. The `StagePill` sits inches
 * away in both places and names the stage in words. That is why a 3px stripe is
 * enough, and why the rail is `aria-hidden` — a screen reader that announced
 * "orange" would be reading the decoration and repeating the pill.
 *
 * ===========================================================================
 * WHY THE RAIL FLIPS TOKEN IN DARK MODE — MEASURED, NOT PREFERRED
 * ===========================================================================
 * The nine `stage/*` pairs are MODE-INVARIANT in Figma (tokens.css header,
 * point 1): the same saturated `…-text` and the same pale `…-bg` in both modes.
 * A pill survives that because it renders BOTH — pale tint behind dark text —
 * so the chip is bright on a dark page either way. A bare rail renders ONE, and
 * neither single token works in both modes:
 *
 *   rail colour     vs #ffffff (light surface)   vs #1f1f1f (dark surface)
 *   ────────────────────────────────────────────────────────────────────────
 *   stage `…-text`  5.07 – 7.10  ✔                2.31 – 3.25  ✘ (2 of 5 < 3:1)
 *   stage `…-bg`    1.12 – 1.22  ✘                13.49 – 14.70 ✔
 *
 * `confirm` (#6d28d9 → 2.31:1) and `lost` (#b91c1c → 2.54:1) are the two that
 * fail on the dark surface; the tints are functionally invisible on the light
 * one. So the rail takes `…-text` in light and `…-bg` in dark, which puts every
 * stage at ≥ 5.07:1 in light and ≥ 13.49:1 in dark against `bg/surface`. WCAG
 * 1.4.11 asks 3:1 of a meaningful non-text element; the worst case here is 1.7×
 * that. Full table in the phase report.
 *
 * NO NEW COLOUR IS INTRODUCED. Both halves are existing `stage/*` tokens — the
 * contract's "the spine must REUSE these", obeyed literally. Nudged foregrounds
 * come through the nudged variable, so the rail can never resurrect a Figma
 * value the AA fix removed.
 *
 * ===========================================================================
 * THE TWO STAGES WITH NO COLUMN — THE CASE THAT WOULD SHIP A BLANK RAIL
 * ===========================================================================
 * `follow_up` and `won_closed` are reachable enum values with no tab, no board
 * column and NO `stage/*` token pair (domain/stages.ts: `tone: 'neutral'`).
 * They still open a peek and a record page, so the rail must answer for them.
 * A `Record<InquiryStage, …>` map is used rather than a lookup with a fallback
 * precisely so a later phase that adds a stage gets a TYPE ERROR here instead of
 * a rail that silently renders transparent.
 *
 * They get `bg/text-secondary`, and that choice is not arbitrary:
 *  · it is exactly the foreground the NEUTRAL STAGE PILL already uses
 *    (`TONE_CLASSES.neutral` = `bg-subtle text-fg-secondary ring-1 ring-line`),
 *    so the rail and the pill beside it agree instead of disagreeing;
 *  · `text/secondary` is one of the SEVEN variables Figma does author per mode
 *    (#6e7580 light, #9a9a9a dark), so it needs no `dark:` half and cannot go
 *    invisible — measured 4.64:1 on white and 5.85:1 on #1f1f1f;
 *  · it says "this stage has no colour", which is true, where any hand-picked
 *    tint would be inventing the tenth stage token the design does not have.
 */

import { STAGES } from '../../domain/stages';
import type { InquiryStage, StageTone } from '../../domain/stages';

/**
 * Tailwind classes for the rail FILL, one per tone.
 *
 * Written as whole literal strings, never composed — Tailwind v4 scans source
 * for complete class names, and `` `bg-stage-${tone}-fg` `` would compile to a
 * rail with no colour at all.
 */
const RAIL_CLASSES: Readonly<Record<StageTone, string>> = {
  new: 'bg-stage-new-fg dark:bg-stage-new-bg',
  quote: 'bg-stage-quote-fg dark:bg-stage-quote-bg',
  confirm: 'bg-stage-confirm-fg dark:bg-stage-confirm-bg',
  advance: 'bg-stage-advance-fg dark:bg-stage-advance-bg',
  lost: 'bg-stage-lost-fg dark:bg-stage-lost-bg',
  // No `stage/*` pair exists; `text/secondary` already flips per mode, so this
  // one needs no `dark:` half. See the header.
  neutral: 'bg-fg-secondary',
};

/** The rail fill for an inquiry's stage. Total over the enum — see the header. */
export function stageRailClass(stage: InquiryStage): string {
  return RAIL_CLASSES[STAGES[stage].tone];
}

/**
 * The rail's geometry, shared by the peek and the record header so the two are
 * one object rendered twice rather than two stripes that drift apart.
 *
 * 3px is the brief's own figure and is a LITERAL, on the precedent already set
 * by `components/ui/Spot.tsx`: the token rule governs colours, spacing steps and
 * durations, and a border width is none of the three (the `space/*` scale is
 * 4·n and has no 3). The COLOUR is a token, which is the part the rule is about.
 */
export const RAIL_GEOMETRY = 'w-[3px] shrink-0';
